import React from 'react';
import { Building, BuildingType, Era } from '../types';

interface BuildingPanelProps {
    buildings: Record<BuildingType, Building>;
    onPurchase: (type: BuildingType) => void;
    currentMoney: number;
    currentEra: Era;
}

const ERA_ORDER: Record<Era, number> = {
    [Era.POND]: 0,
    [Era.VILLAGE]: 1,
    [Era.CITY]: 2,
    [Era.KINGDOM]: 3,
    [Era.EMPIRE]: 4
};

const ERA_NAMES: Record<Era, string> = {
    [Era.POND]: '연못',
    [Era.VILLAGE]: '마을',
    [Era.CITY]: '도시',
    [Era.KINGDOM]: '왕국',
    [Era.EMPIRE]: '제국'
};

const BuildingPanel: React.FC<BuildingPanelProps> = ({
    buildings,
    onPurchase,
    currentMoney,
    currentEra
}) => {
    return (
        <div className="bg-slate-800 md:rounded-xl p-4 md:border-2 border-slate-700 md:max-h-[calc(100vh-180px)] md:overflow-y-auto">
            <h2 className="font-display text-2xl text-yellow-400 mb-4 sticky top-0 bg-slate-800 pb-2 border-b border-slate-700 z-10">
                🏗️ 건설
            </h2>
            <div className="space-y-3">
                {Object.values(buildings).map((building: Building) => {
                    const cost = Math.floor(building.baseCost * Math.pow(building.costMultiplier, building.count));
                    const canAfford = currentMoney >= cost;
                    const isUnlocked = ERA_ORDER[currentEra] >= ERA_ORDER[building.unlockEra];
                    const isMaxed = building.count >= building.maxCount;
                    const isDisabled = !canAfford || !isUnlocked || isMaxed;

                    return (
                        <div
                            key={building.id}
                            className={`p-3 rounded-lg border transition-all duration-200 ${
                                isMaxed
                                    ? 'bg-green-900/30 border-green-700/50'
                                    : !isUnlocked
                                        ? 'bg-slate-900 border-slate-800 opacity-40'
                                        : canAfford
                                            ? 'bg-slate-700 border-slate-600 hover:border-yellow-500 cursor-pointer active:scale-95'
                                            : 'bg-slate-900 border-slate-800 opacity-60 cursor-not-allowed'
                            }`}
                            onClick={() => !isDisabled && onPurchase(building.id)}
                        >
                            <div className="flex justify-between items-start mb-1 gap-2">
                                <div className="flex items-center gap-2 min-w-0 flex-1">
                                    <span className="text-2xl shrink-0">{building.icon}</span>
                                    <div className="min-w-0">
                                        <span className="font-bold text-white break-words">{building.name}</span>
                                        {!isUnlocked && (
                                            <span className="ml-2 text-xs bg-purple-600/50 px-2 py-0.5 rounded text-purple-200 whitespace-nowrap">
                                                🔒 {ERA_NAMES[building.unlockEra]}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span className={`text-xs px-2 py-0.5 rounded shrink-0 ${
                                    isMaxed ? 'bg-green-600 text-white' : 'bg-slate-600 text-slate-300'
                                }`}>
                                    {building.count}/{building.maxCount}
                                </span>
                            </div>

                            <p className="text-xs text-slate-400 mb-2">
                                {building.description}
                            </p>

                            <div className="flex flex-wrap gap-2 mb-2 text-xs">
                                {building.incomePerTick > 0 && (
                                    <span className="bg-green-900/50 text-green-300 px-2 py-0.5 rounded">
                                        +{building.incomePerTick}/초
                                    </span>
                                )}
                                {building.happinessBonus > 0 && (
                                    <span className="bg-pink-900/50 text-pink-300 px-2 py-0.5 rounded">
                                        +{building.happinessBonus}😊
                                    </span>
                                )}
                                {building.populationBonus > 0 && (
                                    <span className="bg-blue-900/50 text-blue-300 px-2 py-0.5 rounded">
                                        +{building.populationBonus}🦆
                                    </span>
                                )}
                                {building.defenseBonus > 0 && (
                                    <span className="bg-orange-900/50 text-orange-300 px-2 py-0.5 rounded">
                                        +{building.defenseBonus}🛡️
                                    </span>
                                )}
                            </div>

                            <div className="flex justify-between items-center">
                                {isMaxed ? (
                                    <span className="text-green-400 font-bold">✓ 최대</span>
                                ) : (
                                    <div className={`font-mono font-bold ${canAfford ? 'text-yellow-400' : 'text-red-400'}`}>
                                        🍩 {cost.toLocaleString()}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default BuildingPanel;
